import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Input, Spin, Tag, message, Empty } from "antd";
import {
  LoadingOutlined, ArrowLeftOutlined, UserOutlined,
  CalendarOutlined, ClockCircleOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { instance } from "utils/axios";

const fmt = (d) => moment(d).format("YYYY-MM-DD HH:mm");
const WEEKDAY = ["Ням", "Дав", "Мяг", "Лха", "Пүр", "Баа", "Бям"];

// group slots by YYYY-MM-DD (preserves order)
function groupByDay(slots) {
  const map = new Map();
  for (const s of slots) {
    const k = moment(s.startTime).format("YYYY-MM-DD");
    if (!map.has(k)) map.set(k, []);
    map.get(k).push(s);
  }
  return Array.from(map.entries()).map(([day, items]) => ({
    day,
    label: `${WEEKDAY[moment(day).day()]} · ${moment(day).format("M/D")}`,
    fullLabel: moment(day).format("YYYY/MM/DD (dddd)"),
    items,
  }));
}

export default function MeetingBooking({ open, onClose, children = [] }) {
  const [advisors, setAdvisors] = useState([]);
  const [loading, setLoading] = useState(false);

  // step: 0 = pick advisor, 1 = pick slot+type+confirm, 2 = success
  const [step, setStep] = useState(0);
  const [advisorId, setAdvisorId] = useState(null);
  const [slotId, setSlotId] = useState(null);
  const [studentId, setStudentId] = useState(children[0]?.id || null);
  const [type, setType] = useState(null);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setStep(0); setAdvisorId(null); setSlotId(null); setType(null); setNote("");
    setStudentId(children[0]?.id || null);
    instance({ method: "get", url: "/guardian/meeting/advisors" })
      .then((res) => setAdvisors(res.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open, children]);

  const selectedAdvisor = advisors.find((a) => a.id === advisorId);
  const grouped = useMemo(
    () => (selectedAdvisor ? groupByDay(selectedAdvisor.slots || []) : []),
    [selectedAdvisor]
  );

  const submit = async () => {
    if (!slotId || !type || !studentId) return;
    setSubmitting(true);
    try {
      await instance({
        method: "post",
        url: `/guardian/meeting/book/${slotId}`,
        data: { studentId, type, note },
      });
      setStep(2);
    } catch (e) {
      message.error(e?.response?.data?.value || "Хүсэлт илгээж чадсангүй");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      title="Их сургуулийн өргөдлийн зөвлөгч багштай уулзалт"
      width={680}
      destroyOnClose
    >
      {loading ? (
        <div className="py-10 flex justify-center">
          <Spin indicator={<LoadingOutlined style={{ fontSize: 28 }} />} />
        </div>
      ) : step === 2 ? (
        // ── Success ──
        <div className="py-8 text-center">
          <div className="text-base text-slate-800 leading-relaxed max-w-md mx-auto">
            Зөвлөгч багшруу уулзалтын хүсэлтийг амжилттай явууллаа. Тун удахгүй
            зөвлөгч багшаас таны сонгосон цагийг баталгаажуулаад хариу мэдэгдэх
            тул та өөрийн и-мэйл хаягаа шалгаарай.
          </div>
          <div className="mt-6">
            <Button type="primary" size="large" onClick={onClose}>Хаах</Button>
          </div>
        </div>
      ) : step === 0 ? (
        // ── Step 1: Advisor cards ──
        <>
          <div className="text-[13px] text-slate-500 mb-3">
            Та таны хүүхдийг хариуцсан зөвлөгч багшийг сонгоно уу:
          </div>
          {advisors.length === 0 ? (
            <Empty description="Зөвлөгч багш алга" />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {advisors.map((a) => {
                const count = a.slots?.length || 0;
                const disabled = count === 0;
                return (
                  <button
                    key={a.id}
                    type="button"
                    disabled={disabled}
                    onClick={() => { setAdvisorId(a.id); setStep(1); }}
                    className={`text-left rounded-lg border p-4 transition-all ${
                      disabled
                        ? "border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed"
                        : "border-slate-200 hover:border-primary hover:shadow-md bg-white"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <UserOutlined className="text-primary" />
                      <div className="font-semibold text-[15px]">
                        {a.lastName} {a.firstName}
                      </div>
                    </div>
                    <div className="text-[12px] text-slate-500 mt-1">
                      {disabled ? "Чөлөөтэй цаг алга" : `${count} чөлөөтэй цаг`}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </>
      ) : (
        // ── Step 2: Pick slot + type ──
        <>
          <div className="mb-4">
            <Button type="link" icon={<ArrowLeftOutlined />} onClick={() => setStep(0)} className="px-0">
              Багш сонгох
            </Button>
            <div className="text-[15px] font-semibold mt-1 flex items-center gap-2">
              <UserOutlined className="text-primary" />
              {selectedAdvisor?.lastName} {selectedAdvisor?.firstName} багштай уулзалт товлох
            </div>
          </div>

          {/* Slots grouped by day */}
          <div className="mb-4">
            <div className="text-[13px] text-slate-500 mb-2 flex items-center gap-1">
              <CalendarOutlined /> Цаг сонгох:
            </div>
            {grouped.length === 0 ? (
              <Empty description="Чөлөөтэй цаг алга" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-72 overflow-y-auto">
                {grouped.map((g) => (
                  <div key={g.day} className="p-3">
                    <div className="text-[13px] font-semibold text-primary mb-2">{g.fullLabel}</div>
                    <div className="flex flex-wrap gap-2">
                      {g.items.map((s) => {
                        const active = slotId === s.id;
                        return (
                          <button
                            key={s.id}
                            type="button"
                            onClick={() => setSlotId(s.id)}
                            className={`px-3 py-1.5 rounded-md border text-[13px] transition-all flex items-center gap-1 ${
                              active
                                ? "border-primary bg-primary text-white font-semibold"
                                : "border-slate-300 hover:border-primary hover:text-primary"
                            }`}
                          >
                            <ClockCircleOutlined />
                            {moment(s.startTime).format("HH:mm")}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Child select (only when multiple) */}
          {children.length > 1 && (
            <div className="mb-4">
              <div className="text-[13px] text-slate-500 mb-2">Хүүхэд:</div>
              <div className="flex flex-wrap gap-2">
                {children.map((c) => {
                  const active = studentId === c.id;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setStudentId(c.id)}
                      className={`px-3 py-1.5 rounded-md border text-[13px] ${
                        active
                          ? "border-primary bg-primary text-white"
                          : "border-slate-300 hover:border-primary"
                      }`}
                    >
                      {c.lastName} {c.firstName}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Type buttons (only after slot picked) */}
          {slotId && (
            <div className="mb-4">
              <div className="text-[13px] text-slate-500 mb-2">Уулзалтын хэлбэр:</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setType("in_person")}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${
                    type === "in_person"
                      ? "border-primary bg-primary/5"
                      : "border-slate-200 hover:border-primary/50"
                  }`}
                >
                  <div className="font-semibold text-[14px]">Биечилсэн</div>
                  <div className="text-[12px] text-slate-500 mt-0.5">Сургуулийн байранд уулзах</div>
                </button>
                <button
                  type="button"
                  onClick={() => setType("online")}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${
                    type === "online"
                      ? "border-primary bg-primary/5"
                      : "border-slate-200 hover:border-primary/50"
                  }`}
                >
                  <div className="font-semibold text-[14px]">Онлайн</div>
                  <div className="text-[11px] text-slate-500 mt-0.5 leading-tight">
                    Зөвхөн орон нутгаас оролцох эцэг эх. Бусад үед биетээр илүүд үзнэ.
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* Optional note */}
          {slotId && type && (
            <div className="mb-4">
              <div className="text-[13px] text-slate-500 mb-2">Тэмдэглэл (заавал биш):</div>
              <Input.TextArea
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Юу ярилцмаар байгаагаа товч бичиж болно"
              />
            </div>
          )}

          {/* Submit */}
          <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-slate-100">
            <Button onClick={onClose}>Болих</Button>
            <Button
              type="primary"
              size="large"
              disabled={!slotId || !type || !studentId}
              loading={submitting}
              onClick={submit}
            >
              Хүсэлт илгээх
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
}

// ─── existing parent's meetings list ─────────────────────────────────────
export function MyMeetings({ refresh = 0 }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setLoading(true);
    instance({ method: "get", url: "/guardian/meeting/my" })
      .then((res) => setData(res.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [refresh]);

  if (loading) return <Spin />;
  if (!data.length) return null;

  return (
    <div className="mt-4">
      <div className="text-[13px] text-slate-500 mb-2">Миний уулзалтууд:</div>
      {data.map((m) => (
        <div key={m.id} className="border border-slate-200 rounded-lg p-3 mb-2 bg-white">
          <div className="text-[14px] font-semibold">
            {m.advisor?.lastName} {m.advisor?.firstName}
            <span className="text-slate-400 font-normal">{" · "}</span>
            {fmt(m.startTime)}
          </div>
          <div className="text-[12px] text-slate-500 mt-1">
            {m.type === "online" ? "Онлайн" : "Биечилсэн"}{" · "}
            {m.status === "confirmed"
              ? <Tag color="green">Баталгаажсан</Tag>
              : <Tag color="gold">Хүсэлт</Tag>}
          </div>
          {m.status === "confirmed" && m.type === "online" && m.meetingLink && (
            <div className="text-[12px] mt-1">
              Линк: <a href={m.meetingLink} target="_blank" rel="noreferrer">{m.meetingLink}</a>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
